import { cn } from '@/lib/utils'
import { BRAND, halftone } from './halftone'

export type DocKind = 'doc' | 'sheet' | 'slide' | 'mail'

const bar = 'block h-[3px] bg-[var(--color-landing-gray-200)]'

/**
 * A document thumbnail drawn in JSX: a page with its title and the shape of
 * its content (text lines, a grid, a slide, an email). Stands in for
 * photography in the fan and the marquee. Decorative: aria-hidden.
 */
export function DocThumb({
  kind,
  title,
  accent = 'blue',
  className,
}: {
  kind: DocKind
  title: string
  accent?: keyof typeof BRAND
  className?: string
}) {
  const color = BRAND[accent]

  if (kind === 'slide') {
    return (
      <div
        aria-hidden
        className={cn('relative flex flex-col justify-between overflow-hidden p-[9%] text-white', className)}
        style={{ backgroundColor: color }}
      >
        <div className="absolute inset-0" style={halftone(0.28, 5)} />
        <span className="relative block h-[3px] w-1/3 bg-white/70" />
        <span className="relative font-[family-name:var(--font-landing-display)] text-[0.7rem] uppercase leading-[1.35]">
          {title}
        </span>
        <span className="relative flex gap-1">
          <span className="block h-[3px] w-1/2 bg-white/60" />
          <span className="block h-[3px] w-1/5 bg-white/40" />
        </span>
      </div>
    )
  }

  return (
    <div
      aria-hidden
      className={cn(
        'flex flex-col gap-[6%] overflow-hidden border border-[var(--color-landing-gray-200)] bg-white p-[9%] dark:border-[var(--color-landing-gray-700)] dark:bg-[#16161f]',
        className
      )}
    >
      <span className="flex items-center gap-1.5">
        <span className="block h-2 w-2 shrink-0" style={{ backgroundColor: color }} />
        <span className="truncate text-[0.6rem] font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] dark:text-white">
          {title}
        </span>
      </span>

      {kind === 'doc' && (
        <>
          <span className="block h-[22%] w-full" style={{ backgroundColor: color, ...halftone(0.3, 4) }} />
          {[100, 92, 96, 70, 88, 60].map((w, i) => (
            <span key={i} className={bar} style={{ width: `${w}%` }} />
          ))}
        </>
      )}

      {kind === 'sheet' && (
        <span className="grid flex-1 grid-cols-3 gap-px bg-[var(--color-landing-gray-200)] dark:bg-[var(--color-landing-gray-700)]">
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className={cn(
                'block bg-white dark:bg-[#16161f]',
                i < 3 && 'bg-[var(--color-landing-gray-100)] dark:bg-[#22222c]'
              )}
              style={i === 10 ? { backgroundColor: color, opacity: 0.85 } : undefined}
            />
          ))}
        </span>
      )}

      {kind === 'mail' && (
        <>
          <span className="flex items-center gap-1">
            <span className="text-[0.5rem] font-semibold text-[var(--color-landing-gray-500)]">To</span>
            <span className={cn(bar, 'w-2/3')} />
          </span>
          <span className="flex items-center gap-1">
            <span className="text-[0.5rem] font-semibold text-[var(--color-landing-gray-500)]">Re</span>
            <span className={cn(bar, 'w-1/2')} />
          </span>
          <span className="block h-px w-full bg-[var(--color-landing-gray-200)]" />
          {[95, 88, 100, 64].map((w, i) => (
            <span key={i} className={bar} style={{ width: `${w}%` }} />
          ))}
          <span className="mt-auto block h-[12%] w-1/3" style={{ backgroundColor: color }} />
        </>
      )}
    </div>
  )
}

export default DocThumb
